export enum ServiceType {
  TIME = "time",
  DAY = "day",
}

export interface TimeSlot {
  startTime?: string;
  endTime?: string;
  duration?: number; // in minutes
  availableSpots?: number; // for car companies
  isAvailable?: boolean;
  appointment?: Appointment | null;
  appointmentStatus?: string | null;
}

export interface Company {
  id: number;
  logo?: string;
  signature?: string | null;
  name: string;
  phoneNumber: string;
  email: string;
  address?: string;
  cnpj?: string | null;
  instagramUrl?: string | null;
  category: "car" | "barbershop";
  createdAt?: Date;
  updatedAt?: Date;
}

export interface Professional {
  id: number;
  companyId: number;
  name: string;
  email?: string;
  picture?: string;
  isActive?: boolean;
  phoneNumber: string;
  createdAt: string;
  updatedAt: string;
}

export interface Service {
  id: number;
  companyId: number;
  professionalId: number | null;
  name: string;
  description?: string;
  duration?: number; // in minutes
  amount?: number;
  price: string;
  /** When set, used for client cars with effort Leve (map Effort.LEVE → priceLight in code) */
  priceLight?: number | null;
  /** When set, used for client cars with effort Médio */
  priceMedium?: number | null;
  /** When set, used for client cars with effort Pesado */
  priceHeavy?: number | null;
  type: ServiceType;
  /** Days until the service is due again; used for client follow-up. */
  expirationDays?: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  professional?: { name: string }; // For display purposes in barbershop
  subServiceRelations: {
    id: number;
    serviceId: number;
    subServiceId: number;
    price: string;
    isActive: boolean;
    isRequired?: boolean;
    subService: SubService;
  }[];
}

export interface SubService {
  id: number;
  companyId: number;
  name: string;
  description?: string;
  /** Days until follow-up; optional per sub-service. */
  expirationDays?: number | null;
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ServiceSubServiceLink {
  id: number;
  serviceId: number;
  subServiceId: number;
  price: number;
  isRequired?: boolean;
  subService?: SubService;
}

export interface SubServiceFormData {
  name: string;
  description?: string;
  expirationDays?: number | null;
}

export interface ProgressStep {
  id: number;
  notes: string;
  startTime: string | null;
  endTime: string | null;
  serviceSubService: {
    id: number;
    isRequired: boolean;
    price: number;
    subService: {
      id: number;
      name: string;
      description?: string;
    };
  };
  professionals?: Professional[];
}

/** Row from GET /dashboard/progress-steps (flat step with nested appointment). */
export interface DashboardProgressStepRow {
  id: number;
  appointmentId: number;
  serviceSubServiceId: number;
  startTime: string | null;
  endTime: string | null;
  notes: string | null;
  isActive?: boolean;
  professionals?: Professional[];
  appointment: {
    id: number;
    clientId: number;
    clientCarId?: number | null;
    serviceId: number;
    appointmentDate: string;
    status: string;
    service: {
      id: number;
      name: string;
      companyId?: number;
      duration?: number;
      price?: string;
      type?: ServiceType;
    };
    client: {
      id: number;
      name: string;
      phoneNumber: string;
    };
    clientCar?: {
      id: number;
      model: string;
      plate: string;
      brand?: string | null;
      effort?: EffortLevel;
    } | null;
  };
  serviceSubService: {
    id: number;
    serviceId: number;
    subServiceId: number;
    price: string | number;
    isRequired: boolean;
    subService: {
      id: number;
      name: string;
      description?: string;
    };
  };
}

export interface ServiceFormData {
  companyId: number;
  professionalId: number | null;
  name: string;
  description?: string;
  duration?: number; // in minutes
  amount?: number;
  price: number;
  type: ServiceType;
}

export type PaymentMethod = "PIX" | "Débito" | "Crédito" | "Dinheiro";

export interface Appointment {
  id: number;
  clientId: number;
  client: {
    id: number;
    name: string;
    phoneNumber: string;
    address?: string;
    birthday?: string;
  };
  clientCarId?: number;
  clientCar?: {
    id: number;
    model: string;
    plate: string;
    brand?: string | null;
    effort?: EffortLevel;
  };
  professionalId: number;
  professionalName?: string; // For display purposes in admin
  serviceId: number;
  appointmentDate: string;
  appointmentEnd?: string | null;
  startTime: string;
  endTime: string;
  price: string;
  status: string;
  notes?: string | null;
  deliveryStartTime?: string | null;
  deliveryEndTime?: string | null;
  payment?: PaymentMethod | null;
  isRecurrency: boolean;
  updatedAt: string;
  createdAt: string;
  service: {
    description: string;
    name: string;
    duration: number;
    price: string;
    type: ServiceType;
    companyId: number;
    priceLight?: number | null;
    priceMedium?: number | null;
    priceHeavy?: number | null;
  };
  progressSteps: ProgressStep[];
  confirmationSent?: boolean;
  professional?: {
    id: number;
    name: string;
  };
}

export interface BookingData {
  companyId?: number;
  date: string;
  clientId?: number;
  phoneNumber?: string;
  clientName?: string; // For creating new clients when they don't exist
  birthday?: string; // Required when creating new clients
  serviceId: number;
  serviceType: ServiceType;
  professionalId?: number;
  startTime?: string; // Optional since it's only needed for time-based services
  subServiceIds?: number[]; // Optional array of selected sub-service IDs
  price?: string; // Optional; when provided (admin create), overrides service default
  clientCarId?: number;
}

export interface WeekAppointmentsMeta {
  companyId: number;
  weekStart: string;
  weekEnd: string;
  count: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: "admin" | "super_admin";
}

export interface AdminAppointmentView extends Appointment {
  clientPhone?: string;
}
export interface Client {
  id: number;
  companyId: number;
  name: string;
  phoneNumber: string;
  birthday?: string;
  address?: string;
  cpf?: string | null;
  appointmentCount: number;
  lastAppointment?: Appointment;
  createdAt: string;
  updatedAt: string;
}

/** Row from GET /client/birthdays (shape may extend with API). */
export interface ClientBirthdayRow {
  id: number;
  name: string;
  phoneNumber?: string;
  birthday?: string;
}

export type EffortLevel = "Leve" | "Médio" | "Pesado";

export interface Car {
  id: number;
  clientId: number;
  model: string;
  plate: string;
  brand?: string | null;
  effort?: EffortLevel;
  createdAt: string;
  updatedAt: string;
}
export interface RecurrencyAttributes {
  id: number;
  companyId: number;
  clientId: number;
  client: Client;
  service: Service;
  serviceId: number;
  clientCarId: number;
  clientCar?: Car | null;
  professionalId?: number | null;
  startTime?: string;
  weeks: number;
  price: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface BalanceByService {
  serviceId: number;
  serviceName: string;
  total: number;
  count: number;
  percentage: number;
}

export interface DailyBalance {
  total: number;
  totalWithoutPayment?: number;
  byPaymentMethod: {
    PIX: number;
    Débito: number;
    Crédito: number;
    Dinheiro: number;
  };
  byService?: BalanceByService[];
}

/** Unpaid appointment embedded in GET /dashboard/balance/monthly `data` */
export interface MonthlyBalanceUnpaidAppointment {
  id: number;
  appointmentDate: string;
  price: number | string;
  status: string;
  client: {
    id: number;
    name: string;
    phoneNumber: string;
  };
  professionalId?: number | null;
  serviceId: number;
  startTime?: string | null;
  endTime?: string | null;
  notes?: string | null;
  service?: {
    name?: string;
    description?: string;
  };
}

export interface MonthlyBalanceData extends DailyBalance {
  appointmentsWithoutPayment?: MonthlyBalanceUnpaidAppointment[];
}

/** Response from GET /dashboard/balance/pending-payments */
export interface PendingPaymentAppointment {
  id: number;
  clientId: number;
  serviceId: number;
  appointmentDate: string;
  startTime?: string | null;
  endTime?: string | null;
  price: number | string;
  status: string;
  notes?: string | null;
  client: {
    id: number;
    name: string;
    phoneNumber: string;
  };
  clientCar?: {
    id: number;
    model: string;
    plate: string;
    brand?: string | null;
  } | null;
  service?: {
    name?: string;
    description?: string;
  };
}

export interface PendingPaymentClientGroup {
  client: {
    id: number;
    name: string;
    phoneNumber: string;
  };
  total: number;
  count: number;
  appointments: PendingPaymentAppointment[];
}

export interface PendingPaymentsByYearData {
  year: number;
  total: number;
  count: number;
  clients: PendingPaymentClientGroup[];
}

/** Row from GET /dashboard/completed-by-effort */
export interface CompletedByEffortRow {
  effort: EffortLevel | string;
  completedCount: number;
  averageDurationMinutes: number | null;
}

/** Response from GET /dashboard/completed-by-effort */
export interface CompletedByEffortSummary {
  month: number;
  year: number;
  totalCompletedWithCar: number;
  byEffort: CompletedByEffortRow[];
}

/** Row from GET /services/follow-up-clients */
export interface FollowUpClientRow {
  client: {
    id: number;
    name: string;
    phoneNumber: string;
    birthday?: string | null;
  };
  clientCar: {
    id: number;
    model: string;
    plate: string;
    brand?: string | null;
  } | null;
  lastAppointmentDate: string;
  lastAppointmentId: number;
  daysSinceLastAppointment: number;
}

/** Service group from GET /services/follow-up-clients */
export interface FollowUpServiceGroup {
  serviceId: number;
  serviceName: string;
  expirationDays: number;
  kind: "service" | "subService";
  clients: FollowUpClientRow[];
}

/** Response payload from GET /services/follow-up-clients */
export interface FollowUpClientsData {
  services: FollowUpServiceGroup[];
}

/** Item from GET /services/follow-up-status (last service date for one client/car) */
export interface FollowUpStatusItem {
  id: number;
  name: string;
  kind: "service" | "subService";
  expirationDays: number;
  lastAppointmentDate: string | null;
  lastAppointmentId: number | null;
  scheduleServiceId: number | null;
  daysSinceLastAppointment: number | null;
  isExpired: boolean;
  clientCarId?: number | null;
  carLabel?: string | null;
}

/** Response payload from GET /services/follow-up-status?clientId= */
export interface FollowUpStatusData {
  items: FollowUpStatusItem[];
}

/** One client bucket from GET /services/follow-up-status (no clientId) */
export interface FollowUpStatusByClient {
  clientId: number;
  items: FollowUpStatusItem[];
}

/** Response payload from GET /services/follow-up-status without clientId */
export interface FollowUpStatusByClientData {
  clients: FollowUpStatusByClient[];
}

/** Monthly revenue goal for a company (GET/PUT /revenue-targets) */
export interface MonthlyRevenueTarget {
  id: number;
  companyId: number;
  month: number;
  year: number;
  amount: number;
}

/** Effort counts from GET /dashboard/cars-completed-throughput */
export interface CarsCompletedEffortCounts {
  leve: number;
  medio: number;
  pesado: number;
}

export interface CarsCompletedThroughputRow {
  period: string;
  byEffort: CarsCompletedEffortCounts;
  total: number;
  byService?: CarsCompletedServiceCount[];
}

export interface CarsCompletedServiceCount {
  serviceId: number;
  serviceName: string;
  byEffort: CarsCompletedEffortCounts;
  total: number;
}

export interface CarsCompletedProgressEffortSpan {
  effort: string;
  averageMinutes: number;
  sampleCount: number;
  estimatedCarsPer8HourDay: number;
}

export interface CarsCompletedProgressServiceSpan {
  serviceId: number;
  serviceName: string;
  overallAverageMinutes: number;
  overallEstimatedCarsPer8HourDay: number;
  sampleCount: number;
  byEffort: CarsCompletedProgressEffortSpan[];
}

export interface CarsCompletedProgressSpan {
  overallAverageMinutes: number;
  overallEstimatedCarsPer8HourDay: number;
  byEffort: CarsCompletedProgressEffortSpan[];
  byService?: CarsCompletedProgressServiceSpan[];
}

/** Response from GET /dashboard/cars-completed-throughput */
export interface CarsCompletedThroughputData {
  startDate: string;
  endDate: string;
  byDay: CarsCompletedThroughputRow[];
  byWeek: CarsCompletedThroughputRow[];
  byMonth: CarsCompletedThroughputRow[];
  progressSpan: CarsCompletedProgressSpan;
  totalCompletedWithCar: number;
}

export type CarsThroughputPeriodGroupBy = "day" | "week" | "month";

export interface CarByEffortClientSummary {
  id: number;
  name: string;
}

export interface CarByEffortCarSummary {
  id: number;
  plate: string;
  model: string;
  effort: string;
  brand?: string | null;
}

export interface CarByEffortItem {
  appointmentId: number;
  appointmentDate: string;
  client: CarByEffortClientSummary;
  clientCar: CarByEffortCarSummary;
  serviceId?: number;
  serviceName?: string;
  totalProgressMinutes: number;
}

export interface CarsByEffortBucket {
  cars: CarByEffortItem[];
}

export interface CarsByEffortPeriodRow {
  period: string;
  byEffort: Record<keyof CarsCompletedEffortCounts, CarsByEffortBucket>;
}

/** Response from GET /dashboard/cars-by-effort (grouped by period) */
export interface CarsByEffortData {
  startDate: string;
  endDate: string;
  groupBy: CarsThroughputPeriodGroupBy;
  periods: CarsByEffortPeriodRow[];
}

export type BudgetStatus = "Created" | "Completed" | "Cancelled";

export interface BudgetSubServiceLine {
  id?: number;
  serviceSubServiceId?: number;
  subServiceId: number;
  name: string;
  description?: string;
  price: number | string;
  isRequired?: boolean;
}

export interface BudgetServiceLine {
  id?: number;
  budgetId?: number;
  serviceId: number;
  price: number | string;
  createdAt?: string;
  updatedAt?: string;
  service?: Pick<Service, "id" | "name" | "description" | "price" | "duration">;
  subServices?: BudgetSubServiceLine[];
}

export interface Budget {
  id: number;
  clientId: number;
  clientCarId?: number | null;
  companyId?: number;
  status: BudgetStatus;
  client?: Pick<Client, "id" | "name" | "phoneNumber">;
  clientCar?: Pick<Car, "id" | "model" | "plate" | "effort" | "brand">;
  /** Normalized from API `items` for use in the UI */
  services?: BudgetServiceLine[];
  /** Raw API field — prefer `services` after normalization */
  items?: BudgetServiceLine[];
  total?: number | string;
  createdAt: string;
  updatedAt: string;
}

export interface BudgetListMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface BudgetListParams {
  clientId?: number;
  status?: BudgetStatus;
  page?: number;
  limit?: number;
}

export interface BudgetCreatePayload {
  clientId: number;
  clientCarId?: number | null;
  services: {
    serviceId: number;
    price: number;
    subServiceIds?: number[];
  }[];
}

export interface Expense {
  id: number;
  companyId: number;
  description: string;
  amount: number;
  category?: string | null;
  expenseDate: string;
  isRecurrent: boolean;
  recurrenceType?: string | null;
  recurrenceGroup?: string | null;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ExpenseMonthSummary {
  total: number;
  byCategory: Record<string, number>;
  count: number;
  month: number;
  year: number;
}

export interface BudgetUpdatePayload {
  clientCarId?: number | null;
  services?: {
    serviceId: number;
    price: number;
    subServiceIds?: number[];
  }[];
  status?: "Completed";
}
